"use client";
import { useQuery } from "@tanstack/react-query";
import { Content, fetchOneEntry, isEditing, BuilderContent, GetContentOptions } from "@builder.io/sdk-react/edge";
import { PublicConfig } from "@/shared/PublicConfig";
import { useSearchParams } from "next/navigation";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PersonalizationAttributes } from "@/shared/types/PersonalizationAttributes";
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/shared/components/ui";
import Link from "next/link";
import isEqual from "react-fast-compare";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/pro-solid-svg-icons";
import dynamic from "next/dynamic";
import { JSONFormProps } from "@/shared/components/JSONForm/JSONForm";
import { FieldValues } from "react-hook-form";
import { PartialQuoteSchema, Quote } from "../types/Quote.interface";
import { BuilderUtils, BuilderDataType } from "../utils/BuilderUtils";
import { useDirtyValues } from "@/shared/hooks/useDirtyValues";
import { UIUtils } from "../utils/UIUtils";
import { TrustPilotData } from "@/shared/components/TrustPilotCarousel/TrustPilotTypes";
import parse from "html-react-parser";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useBuilderTestActions, useBuilderTestIds, useBuilderVariationIds } from "../hooks/useBuilderStore";
import PromoCard, { PromoCardProps } from "./PromoCard";
import { PolicyCalloutProps } from "@/shared/components/PolicyCallout";
import { useAppLayerContext } from "@/shared/contexts/AppLayer";
import VariantPetStepHero from "./PolicyCalloutTest/VariantPetStepHero";
import AggregatorComparisonTable from "./PolicyCalloutTest/AggregatorComparisonTable";
import SimplifiReassurance from "./PolicyCalloutTest/SimplifiReassurance";
import SimplifiLifestyle from "./PolicyCalloutTest/SimplifiLifestyle";
import { EndorsementBannerProps } from "@/shared/components/EndorsementBanner";
import MarqueeBanner, { MarqueeBannerProps } from "@/shared/components/MarqueeBanner";

// put this near the top of the file (below imports) or right above the custom component config
const asUrl = (v?: any): string | undefined => {
    if (!v) return undefined;
    if (typeof v === "string") return v;
    // Builder file/image objects commonly look like { url: "..." } or { value: { src: "..." } }
    if (typeof v.url === "string") return v.url;
    if (typeof v.value?.src === "string") return v.value.src;
    if (typeof v.src === "string") return v.src;
    return undefined;
};
import VariantCoverage from "./PolicyCalloutTest/VariantCoverage";
import EverySixSeconds from "./ui/everySixSeconds";
import Strings from "../utils/Strings.constants";

// Dynamic import for JSONForm component
const JSONForm = dynamic(() => import("@/shared/components/JSONForm/JSONForm"));
const TrustPilotSlotContent = dynamic(() => import("@/shared/components/TrustPilotCarousel/TrustPilotSlotContent"));
const FaqModule = dynamic(() => import("@/quote-ptz-us/src/components/FaqModule"), { ssr: true });
const PolicyCallout = dynamic(() => import("@/shared/components/PolicyCallout"));
const EndorsementBanner = dynamic(() => import("@/shared/components/EndorsementBanner"));

export interface CustomizationSlotProps {
    type:
        | "co-brand"
        | "form-header"
        | "form-footer"
        | "modal"
        | "symbol"
        | "announcement-bar"
        | "above-cta"
        | "above-disclaimer"
        | "terms-disclaimer"
        | "thank-you"
        | "thank-you-sidebar"
        | "above-preventive"
        | "retargeting-form-copy"
        | "icon-grid"
        | "disclaimers-footer"
        | "disclaimers-above-footer"
        | "above-card"
        | "below-stepper";
    initialContent?: BuilderContent;
    modalData?: FieldValues;
    formData?: FieldValues;
    updateData?: (dataArray: { type: BuilderDataType; data: FieldValues }[]) => Promise<void>;
    data?: Quote;
    formId?: string;
    formStepId?: string;
    fallback?: React.ReactNode;
    priorityCode?: string;
    activePetIndex?: number;
}

export type BuilderContentMap = Record<CustomizationSlotProps["type"], BuilderContent | undefined>;

export function CustomizationSlot(props: CustomizationSlotProps) {
    const { type, initialContent, modalData, formData, updateData, data: formValues, formId, formStepId, fallback, priorityCode, activePetIndex } = props;

    const { dirtyValues } = useDirtyValues();
    const data = useMemo(() => UIUtils.deepMerge(formValues ?? {}, dirtyValues ?? {}, PartialQuoteSchema).mergedBase, [formValues, dirtyValues]);
    const searchParams = useSearchParams();
    const lastAttributes = useRef<PersonalizationAttributes | undefined>(undefined);
    const editing = isEditing(searchParams);
    const isEditingType = editing && searchParams.get("builder.preview") === props.type;
    // The entry rendered on the last completed fetch, for the "attributes haven't changed" short
    // circuit below. A ref, not state: `content` is derived from the query result now.
    const lastContentRef = useRef<BuilderContent | null>(null);
    // Which display-once group the customer has closed this session. The persisted flag written by
    // `handleModalClose` only comes back through `modalData` after the quote update round-trips.
    const [dismissedModalGroupID, setDismissedModalGroupID] = useState<string | undefined>(undefined);
    const builderTestIds = useBuilderTestIds();
    const builderVariationIds = useBuilderVariationIds();
    const { addTestData } = useBuilderTestActions();
    const { appState } = useAppLayerContext();
    const { referrer, disableBuilderSlots } = appState;

    const attributes = BuilderUtils.getBuilderAttributes({
        data,
        formId,
        formStepId,
        priorityCode,
        builderTestIds,
        builderVariationIds,
        activePetIndex
    });
    attributes.queryParamsStringLowerCase = searchParams.toString().toLowerCase();
    attributes.utmSourceLowerCase = UIUtils.getCaseInsensitiveValue(searchParams, "utm_source")?.toLowerCase();
    attributes.referrer = referrer;

    const shouldDisplayModal = useCallback(
        (modalGroupID: string): boolean => {
            const modalStates = modalData ?? {};
            return !modalStates[modalGroupID];
        },
        [modalData]
    );

    const fetchBuilderContent = async () => {
        if (!PublicConfig.IS_BUILDER_ENABLED || disableBuilderSlots) {
            return null;
        }

        if (isEqual(attributes, lastAttributes.current)) {
            return lastContentRef.current;
        }

        lastAttributes.current = attributes;

        try {
            const entryOptions: GetContentOptions = {
                apiKey: PublicConfig.BUILDER_IO_PUBLIC_API_KEY,
                model: type,
                userAttributes: attributes
            };

            if (PublicConfig.ENVIRONMENT !== "production") {
                entryOptions.cacheSeconds = 0;
                entryOptions.staleCacheSeconds = 0;
                entryOptions.options = {
                    cachebust: true
                };
            }

            const result = await fetchOneEntry(entryOptions);

            return result;
        } catch (error) {
            throw error;
        }
    };

    const {
        data: builderData,
        error,
        isSuccess
    } = useQuery({
        queryKey: ["builderContent", type, formId, formStepId, attributes],
        queryFn: fetchBuilderContent,
        enabled: !disableBuilderSlots,
        retry: false,
        staleTime: Infinity
    });

    const addBuilderTestData = useCallback(
        (content: BuilderContent | null) => {
            if (content?.id && content?.testVariationId) {
                addTestData(content.id, content.testVariationId);
            }
        },
        [addTestData]
    );

    // The entry the query resolved to IS the content — no copy of it in state, so it renders in the
    // same pass the query settles instead of one commit later.
    const content = isSuccess && builderData ? builderData : null;
    const displayOnceGroupID = content?.data?.displayOnceGroupId ?? content?.id;
    // A display-once modal is open until either the customer closes it (local) or the quote comes back
    // carrying the flag (`modalData`, via `shouldDisplayModal`).
    const modalOpen =
        !!content?.data?.displayOnce && !!displayOnceGroupID && !!updateData && shouldDisplayModal(displayOnceGroupID) && dismissedModalGroupID !== displayOnceGroupID;

    useEffect(() => {
        lastContentRef.current = content;

        // Track test data with our optimized method
        if (content) {
            addBuilderTestData(content);
        }
    }, [content, addBuilderTestData]);

    const handleModalClose = async () => {
        if (editing) return;

        setDismissedModalGroupID(displayOnceGroupID);
        const formGroupID = displayOnceGroupID;
        if (content?.data?.displayOnce && formGroupID && updateData) {
            // If this modal is set to only display once, then make sure we save the state as part of the quote extra data.
            await updateData([{ type: "modal", data: { ...modalData, [formGroupID]: true } }]);
        }
    };

    const handleFormSubmit = async (jsonFormProps: JSONFormProps, newFormData: any) => {
        if (updateData) {
            const formUpdateData: { type: BuilderDataType; data: FieldValues } = { type: "form", data: { ...formData, [jsonFormProps.formID]: newFormData } };
            const updates = [formUpdateData];

            if (type === "modal") {
                const formGroupID = displayOnceGroupID;
                const modalUpdateData: { type: BuilderDataType; data: FieldValues } = { type: "modal", data: { ...modalData, [formGroupID as string]: true } };
                updates.push(modalUpdateData);
            }

            await updateData(updates);
            if (type === "modal") {
                setDismissedModalGroupID(displayOnceGroupID);
            }
        }
    };

    const JSONFormCustomComponent = {
        name: "JSON Form",
        inputs: [
            { name: "formID", type: "text", friendlyName: "Form ID", required: true },
            { name: "dataSchema", type: "text", friendlyName: "Data Schema" },
            { name: "uiSchema", type: "text", friendlyName: "UI Schema" },
            { name: "submitButtonTitle", type: "text", friendlyName: "Submit Button Title" }
        ],
        component: (props: JSONFormProps) => <JSONForm {...props} initialData={formData?.[props.formID]} onSubmit={data => handleFormSubmit(props, data)} />
    };

    const TrustPilotCustomComponent = {
        name: "TrustPilot Carousel",
        inputs: [
            { name: "data", type: "object", friendlyName: "TrustPilot Data" },
            { name: "underwriter", type: "text", friendlyName: "Underwriter/Locale", enum: ["US", "CA"] }
        ],
        component: (props: { data: TrustPilotData; underwriter: string }) => <TrustPilotSlotContent data={props.data} underwriter={props.underwriter} />
    };

    const FaqModuleCustomComponent = {
        name: "FAQ Module",
        inputs: [
            {
                name: "faqs",
                type: "list",
                subFields: [
                    { name: "question", type: "string", friendlyName: "Question" },
                    {
                        name: "answer",
                        type: "html",
                        friendlyName: "Answer"
                    }
                ],
                friendlyName: "FAQs"
            }
        ],
        component: (props: { faqs?: { question: string; answer: string }[] }) => {
            const faqsWithParsedHtml = props.faqs?.map(faq => ({
                ...faq,
                answer: parse(faq.answer || "")
            }));

            return <FaqModule faqs={faqsWithParsedHtml} />;
        }
    };

    const PolicyCalloutCustomComponent = {
        name: "Policy Callout",
        models: ["retargeting-form-copy"],
        inputs: [
            {
                name: "title",
                type: "string",
                friendlyName: "Title"
            },
            {
                name: "subTitle",
                type: "string",
                friendlyName: "Sub Title"
            },
            {
                name: "description",
                type: "html",
                friendlyName: "Description"
            },
            {
                name: "blockquoteDescription",
                type: "string",
                friendlyName: "Blockquote Description"
            },
            {
                name: "blockquotes",
                friendlyName: "Blockquotes",
                type: "list",
                subFields: [
                    { name: "topText", type: "html", friendlyName: "Top Text" },
                    { name: "bottomText", type: "html", friendlyName: "Bottom Text" }
                ]
            }
        ],
        component: (props: PolicyCalloutProps) => {
            const { blockquotes, title, subTitle, description, blockquoteDescription } = props;

            const parsedBlockquotes = blockquotes?.map(blockquote => ({
                ...blockquote,
                topText: typeof blockquote.topText === "string" ? parse(blockquote.topText) : blockquote.topText,
                bottomText: typeof blockquote.bottomText === "string" ? parse(blockquote.bottomText) : blockquote.bottomText
            }));
            const parsedDescription = typeof description === "string" ? parse(description) : description;
            return (
                <PolicyCallout title={title} subTitle={subTitle} description={parsedDescription} blockquotes={parsedBlockquotes} blockquoteDescription={blockquoteDescription} />
            );
        }
    };

    const PromoCardCustomComponent = {
        name: "Promo Card",
        inputs: [
            {
                name: "cornerText",
                type: "string",
                friendlyName: "Corner Text"
            },
            {
                name: "iconText",
                type: "string",
                friendlyName: "Icon Text"
            },
            {
                name: "content",
                type: "html",
                friendlyName: "Content"
            },
            {
                name: "footerText",
                type: "html",
                friendlyName: "Footer Text"
            }
        ],
        component: (props: PromoCardProps) => {
            const { cornerText, iconText, content, footerText } = props;
            const parsedContent = parse(String(content ?? ""));
            const parsedFooter = parse(String(footerText ?? ""));
            return <PromoCard cornerText={cornerText} iconText={iconText} content={parsedContent} footerText={parsedFooter} />;
        }
    };

    const WhySpotPolicyContent = {
        name: "'Why Spot?' Policy Content",
        models: ["retargeting-form-copy"],
        component: () => {
            return <VariantPetStepHero variant="whySpot" />;
        }
    };

    const AggregatorComparisonTableComponent = {
        name: "Aggregator Comparison Table",
        inputs: [
            {
                name: "firstFeatureText",
                type: "string",
                friendlyName: "First Feature Text",
                defaultValue: "Access to partner perks",
                helperText: "Text for the first feature row (e.g., 'Access to partner perks' or '$2,500+ in partner perks')"
            },
            {
                name: "title",
                type: "string",
                friendlyName: "Title",
                defaultValue: Strings.POLICY_CALLOUT_CONTENT.titles.whySpot,
                helperText: "The main heading. Leave blank to hide."
            },
            {
                name: "tagline",
                type: "string",
                friendlyName: "Tagline",
                defaultValue: Strings.POLICY_CALLOUT_CONTENT.tagline,
                helperText: "The tagline above the title. Leave blank to hide."
            },
            {
                name: "showAccolades",
                type: "boolean",
                friendlyName: "Show Accolades",
                defaultValue: true,
                helperText: "Toggle the accolades section on or off."
            },
            {
                name: "useWideColumns",
                type: "boolean",
                friendlyName: "Use Wide Columns",
                defaultValue: false,
                helperText: "Toggles between default (narrow) and wide (52px/132px/182px) columns."
            },
            {
                name: "whySpotBadgeSuperscript",
                type: "string",
                friendlyName: "'Why Spot?' Badge Superscript",
                defaultValue: "",
                helperText: "Superscript text for the 'Why Spot?' badge."
            }
        ],
        component: (props: {
            firstFeatureText?: string;
            title?: string;
            tagline?: string;
            showAccolades?: boolean;
            useWideColumns?: boolean;
            whySpotBadgeSuperscript?: string;
        }) => {
            return <AggregatorComparisonTable {...props} />;
        }
    };

    const EverySixSecondsComponent = {
        name: "Every Six Seconds",
        inputs: [
            {
                name: "wrapperClass",
                type: "string",
                friendlyName: "Wrapper Class",
                helperText: "Override wrapper styles"
            }
        ],
        component: () => {
            return <EverySixSeconds />;
        }
    };

    const EndorsementBannerComponent = {
        name: "Endorsement Banner",
        models: ["above-card"],
        inputs: [
            // Logos - responsive sizes
            { name: "logoMobile", type: "file", friendlyName: "logoMobile" },
            { name: "logoDesktop", type: "file", friendlyName: "logoDesktop" },
            { name: "borderColor", type: "color", friendlyName: "Divider Border Color (Mobile/Tablet)" },

            // Headline - different images per viewport
            { name: "headlineImageUrlMobile", type: "file", friendlyName: "Headline Image (Mobile/Tablet)" },
            { name: "headlineImageUrlDesktop", type: "file", friendlyName: "Headline Image (Desktop)" },
            { name: "mobileBadgeImageUrl", type: "file", friendlyName: "Badge Image sm and md" },
            { name: "desktopBadgeImageUrl", type: "file", friendlyName: "Badge Image lg and up" },
            { name: "ratingLogoUrl", type: "file", friendlyName: "Rating Logo (Desktop only)" },

            // Collapse
            { name: "collapsedByDefault", type: "boolean" },
            { name: "expandLabel", type: "string" },
            { name: "collapseLabel", type: "string" },

            // Colors
            { name: "bgColor", type: "color" },
            { name: "accentBgColor", type: "color" },

            // Features
            {
                name: "features",
                type: "list",
                subFields: [
                    { name: "label", type: "string", required: true },
                    { name: "iconUrl", type: "file" },
                    { name: "textColor", type: "color" }
                ]
            },

            // Layout variant
            { name: "variant", type: "string", enum: ["default", "overlay"], defaultValue: "default", friendlyName: "Layout Variant" },

            // Review - 3 separate images
            { name: "reviewImageUrlMobile", type: "file", friendlyName: "Review Image (Mobile)" },
            { name: "reviewImageUrlTablet", type: "file", friendlyName: "Review Image (Tablet)" },
            { name: "reviewImageUrlDesktop", type: "file", friendlyName: "Review Image (Desktop)" },

            // collapse/expand icons
            { name: "expandIconUrl", type: "file", friendlyName: "Expand Icon" },
            { name: "collapseIconUrl", type: "file", friendlyName: "Collapse Icon" }
        ],
        component: (raw: EndorsementBannerProps) => {
            const normalized: EndorsementBannerProps = {
                variant: raw.variant || "default",
                logoMobile: asUrl(raw.logoMobile),
                logoDesktop: asUrl(raw.logoDesktop),
                ratingLogoUrl: asUrl(raw.ratingLogoUrl),
                headlineImageUrlMobile: asUrl(raw.headlineImageUrlMobile),
                headlineImageUrlDesktop: asUrl(raw.headlineImageUrlDesktop),
                mobileBadgeImageUrl: asUrl(raw.mobileBadgeImageUrl),
                desktopBadgeImageUrl: asUrl(raw.desktopBadgeImageUrl),
                collapsedByDefault: raw.collapsedByDefault,
                expandLabel: raw.expandLabel,
                collapseLabel: raw.collapseLabel,
                bgColor: raw.bgColor,
                accentBgColor: raw.accentBgColor,
                borderColor: raw.borderColor,
                features: (raw.features ?? []).map(f => ({
                    label: f.label,
                    iconUrl: asUrl(f.iconUrl) ?? "",
                    textColor: f.textColor
                })),
                reviewImageUrlMobile: asUrl(raw.reviewImageUrlMobile),
                reviewImageUrlTablet: asUrl(raw.reviewImageUrlTablet),
                reviewImageUrlDesktop: asUrl(raw.reviewImageUrlDesktop),
                expandIconUrl: asUrl(raw.expandIconUrl),
                collapseIconUrl: asUrl(raw.collapseIconUrl),
                forceOpenInEditor: editing
            };

            return <EndorsementBanner {...normalized} />;
        }
    };

    const MarqueeBannerComponent = {
        name: "Marquee Banner",
        models: ["below-stepper"],
        inputs: [
            // Logos
            { name: "logoMobile", type: "file", friendlyName: "Logo (Mobile)" },
            { name: "logoDesktop", type: "file", friendlyName: "Logo (Desktop)" },

            // Colors
            { name: "bgColor", type: "color", friendlyName: "Background Color" },
            { name: "accentBgColor", type: "color", friendlyName: "Accent Background Color" },

            // Features
            {
                name: "features",
                type: "list",
                subFields: [
                    { name: "label", type: "string", required: true },
                    { name: "iconUrl", type: "file" },
                    { name: "textColor", type: "color" }
                ],
                friendlyName: "Features"
            },

            // Wedge offsets
            { name: "wedgeOffset", type: "number", friendlyName: "Wedge Offset % (base)", defaultValue: 93 },
            { name: "wedgeOffsetSm", type: "number", friendlyName: "Wedge Offset % (sm)" },
            { name: "wedgeOffsetMd", type: "number", friendlyName: "Wedge Offset % (md)" },
            { name: "wedgeOffsetLg", type: "number", friendlyName: "Wedge Offset % (lg)" },
            { name: "wedgeOffsetXl", type: "number", friendlyName: "Wedge Offset % (xl+)" },

            // Marquee speed
            { name: "speedSeconds", type: "number", friendlyName: "Marquee Speed (seconds)", defaultValue: 40 }
        ],
        component: (raw: MarqueeBannerProps) => {
            const normalized: MarqueeBannerProps = {
                logoMobile: asUrl(raw.logoMobile),
                logoDesktop: asUrl(raw.logoDesktop),
                bgColor: raw.bgColor,
                accentBgColor: raw.accentBgColor,
                features: (raw.features ?? []).map(f => ({
                    label: f.label,
                    iconUrl: asUrl(f.iconUrl) ?? "",
                    textColor: f.textColor
                })),
                wedgeOffset: raw.wedgeOffset,
                wedgeOffsetSm: raw.wedgeOffsetSm,
                wedgeOffsetMd: raw.wedgeOffsetMd,
                wedgeOffsetLg: raw.wedgeOffsetLg,
                wedgeOffsetXl: raw.wedgeOffsetXl,
                speedSeconds: raw.speedSeconds
            };

            return <MarqueeBanner {...normalized} />;
        }
    };

    const AggregatorCoveragePreview = {
        name: "Aggregator Coverage Preview",
        models: ["retargeting-form-copy"],
        component: () => {
            return <VariantCoverage />;
        }
    };

    const SimplifiReassuranceComponent = {
        name: "Simplifi Reassurance",
        models: ["retargeting-form-copy"],
        component: () => {
            return <SimplifiReassurance />;
        }
    };

    const SimplifiLifestyleComponent = {
        name: "Simplifi Lifestyle",
        models: ["retargeting-form-copy"],
        component: () => {
            return <SimplifiLifestyle />;
        }
    };

    const renderContent = () => {
        if (!content && !initialContent) {
            return null;
        }

        return (
            <section aria-label={content?.name}>
                <Content
                    apiKey={PublicConfig.BUILDER_IO_PUBLIC_API_KEY}
                    content={content ?? initialContent}
                    model={props.type}
                    linkComponent={Link}
                    data={attributes}
                    customComponents={[
                        TrustPilotCustomComponent,
                        JSONFormCustomComponent,
                        FaqModuleCustomComponent,
                        PolicyCalloutCustomComponent,
                        PromoCardCustomComponent,
                        WhySpotPolicyContent,
                        AggregatorComparisonTableComponent,
                        EndorsementBannerComponent,
                        MarqueeBannerComponent,
                        AggregatorCoveragePreview,
                        EverySixSecondsComponent,
                        SimplifiReassuranceComponent,
                        SimplifiLifestyleComponent
                    ]}
                />
            </section>
        );
    };

    const renderModal = () => {
        return (
            <Dialog open={editing || modalOpen} modal={!editing}>
                <DialogContent className={`max-w-screen-md ${content?.data?.noPadding ? "md:p-0" : ""} ${editing ? "border-8" : ""}`}>
                    <VisuallyHidden asChild>
                        <DialogTitle>{content?.data?.title ?? ""}</DialogTitle>
                    </VisuallyHidden>
                    <VisuallyHidden asChild>
                        <DialogDescription />
                    </VisuallyHidden>
                    {content?.data?.allowManualClose && (
                        <DialogHeader className="m-0 contents p-0">
                            <DialogClose onClick={handleModalClose} className="z-30 text-content-primary-invert">
                                <FontAwesomeIcon icon={faTimes} className="inline-block size-6" size="lg" />
                            </DialogClose>
                        </DialogHeader>
                    )}
                    {renderContent()}
                </DialogContent>
            </Dialog>
        );
    };

    if ((!!error && !!fallback) || (!initialContent && !content && !!fallback)) {
        return <>{fallback}</>;
    }

    if (!editing && !content && !initialContent) {
        return null;
    }

    if (editing && !isEditingType) {
        return null;
    }

    return type === "modal" ? renderModal() : renderContent();
}
